import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getParts,
  createStockItemAndPart,
  addPartToStockItem,
  updateParts,
  deleteParts,
  getPartsById,
  checkDuplicatePart,
} from '../api/partsAPI';
import { Icon } from '@iconify/react';
import QRCode from 'react-qr-code';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useTableFilters } from '../utils/useTableFilters';
import * as XLSX from 'xlsx';
import PartsDataTable from './PartsDataTable';
import { useSetting } from '../utils/AppSettingsContext';
import DocumentDataTable from './DocumentDataTable';
import { useValidator } from '../utils/useValidator';
import { getNextStockItemNumber, getStockItemsById } from '../api/stock-itemsAPI';
import { createStockItemsQuantities } from '../api/stock-items-quantitiesAPI';
import { createRotable, deleteRotable, getRotableById, updateRotable } from '../api/rotablesAPI';
import RotableFormModal from './parts/RotableFormModal';
import { useModule } from '../utils/useModule';
import { getPaginatedSuppliers } from '../api/supplierAPI';

const PartsListLayer = () => {
  const userProfile = useUserProfile();
  const basePartState = {
    categoryId: 25,
    stockedItem: 0,
    quantity: 1,
    measurementUnitId: 5,
    serialised: 0,
    files: [],
    weight: 0.0,
    lastCost: 0.0,
  };
  const getDefaultPartState = () => ({
    ...basePartState,
    pricingDate: new Date().toISOString().split('T')[0],
  });
  const [parts, setParts] = useState([]);
  const [partPage, setPartPage] = useState(1);
  const [partHasMore, setPartHasMore] = useState(true);
  const [partLoading, setPartLoading] = useState(false);
  const [newPart, setNewPart] = useState(getDefaultPartState());
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [createStockItem, setCreateStockItem] = useState(false);
  const [stockOption, setStockOption] = useState('partOnly');
  const [refreshToken, setRefreshToken] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [showRotableModal, setShowRotableModal] = useState(false);
  const [rotables, setRotables] = useState([]);
  const [pendingRotableIds, setPendingRotableIds] = useState([]);
  const [linkedStockItem, setLinkedStockItem] = useState(null);
  const [linkedStockQuantities, setLinkedStockQuantities] = useState([]);
  const listContainerRef = useRef(null);
  const weightUom = useSetting('weight_uom');
  const isLinkedStockItemEdit = Boolean(editingId && newPart?.stockItemId !== null && newPart?.stockItemId !== undefined);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const useRotables = useModule('rotables');

  const showStockItemFields =
    stockOption === 'createStock' || Boolean(newPart?.stockedItem);

  const handleSupplierSearch = useCallback(async (inputValue = '', { page = 1 } = {}) => {
    try {
      const trimmedSearch = typeof inputValue === 'string' ? inputValue.trim() : '';
      const initialPageSize = 10;
      const lazyPageSize = 5;
      const pageSize = page === 1 ? initialPageSize : lazyPageSize;
      const apiPage = page === 1 ? 1 : page + 1;
      const params = { page: apiPage, pageSize };

      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const result = await getPaginatedSuppliers(params);
      const items = result.data || result;
      const options = items.map((supplier) => ({
        value: supplier.id,
        label: supplier.name,
        raw: supplier,
      }));

      const total =
        typeof result.total === 'number'
          ? result.total
          : typeof result.count === 'number'
            ? result.count
            : undefined;
      const loadedCount =
        page === 1
          ? initialPageSize
          : initialPageSize + (page - 1) * lazyPageSize;
      const hasMore =
        typeof total === 'number'
          ? loadedCount < total
          : options.length === pageSize;

      return { options, hasMore };
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      return { options: [], hasMore: false };
    }
  }, []);

  const generalFields = useMemo(() => {
    const fields = [
      {
        field: 'partNumber',
        label: 'Part Number',
        type: 'text',
        size: '3',
        required: true,
        maxLength: 45,
      },
      {
        field: 'description',
        label: 'Description',
        type: 'text',
        size: '9',
        required: true,
        maxLength: 100,
      },
      {
        field: 'categoryId',
        label: 'Part Category',
        type: 'select',
        size: '3',
        required: true,
        api: 'parts-category',
        displayField: 'name',
      },
      { field: 'oemNumber', label: 'Supplier Part Number', type: 'text', size: '3', maxLength: 45 },
      {
        field: 'sourceOfSupplyId',
        label: 'Part Manufacturer',
        type: 'select',
        size: '3',
        api: 'manufacturers',
        displayField: 'manufacturer',
      },
      {
        field: 'supplierId',
        label: 'Preferred Supplier',
        type: 'paginated-select',
        size: '3',
        loadOptions: handleSupplierSearch,
        selectedOption: selectedSupplier,
        setSelectedOption: setSelectedSupplier,
        isClearable: true,
        minSearchLength: 2,
      },
      {
        field: 'lastCost',
        label: 'Part Price',
        type: 'number',
        size: '3',
        mustBePositiveDecimal: true,
        maxDecimalPlaces: 2,
      },
      {
        field: 'pricingDate',
        label: 'Pricing Date',
        type: 'date',
        size: '3',
        noFutureDate: true,
        max: new Date().toISOString().split('T')[0],
      },

    ];

    {/*if (showStockItemFields) {
      fields.splice(8, 0, {
        field: 'materialNumber',
        label: 'Material Number',
        type: 'text',
        size: '3',
        maxLength: 45,
        disabled: true,
      });
    }*/}

    return fields;
  }, [showStockItemFields, handleSupplierSearch, selectedSupplier]);

  const stockItemFields = useMemo(
    () => [
      {
        field: 'quantity',
        label: 'Quantity',
        type: 'number',
        size: '2',
        required: true,
        disabled: isLinkedStockItemEdit,
      },
      {
        field: 'measurementUnitId',
        label: 'Units',
        type: 'select',
        size: '2',
        api: 'measurement-units',
        displayField: 'unitCode',
        required: true,
        disabled: isLinkedStockItemEdit,
      },
      { field: 'consignment', label: 'Consignment Stock', type: 'switch', size: '2' },
      {
        field: 'spacer2',
        label: '',
        size: 5,
      },
      {
        field: 'locationId',
        label: 'Location',
        type: 'select',
        size: '4',
        api: 'locations',
        displayField: 'name',
        required: true,
      },
      { field: 'zone', label: 'Zone', type: 'text', size: '2', maxLength: 15 },
      { field: 'aisle', label: 'Aisle', type: 'text', size: '2', maxLength: 15 },
      { field: 'bay', label: 'Bay', type: 'text', size: '2', maxLength: 15 },
      { field: 'shelf', label: 'Shelf', type: 'text', size: '2', maxLength: 15 },
      {
        field: 'stockMin',
        label: 'Min. Stock',
        type: 'number',
        size: '3',
        mustBePositiveInteger: true,
      },
      {
        field: 'stockMax',
        label: 'Max. Stock',
        type: 'number',
        size: '3',
        mustBePositiveInteger: true,
      },
      {
        field: 'reorderPoint',
        label: 'Reorder Point',
        type: 'number',
        size: '3',
        mustBePositiveInteger: true,
      },
      {
        field: 'cycleCountFreq',
        label: 'Stock Take Frequency (Days)',
        type: 'number',
        size: '3',
        mustBePositiveInteger: true,
      },
      {
        field: 'locationCode',
        label: 'Location Code',
        type: 'text',
        size: '4',
        maxLength: 45,
      },
    ],
    [isLinkedStockItemEdit],
  );

  const existingStockFields = useMemo(
    () => [
      {
        field: 'stockItemId',
        label: 'Existing Stock Item',
        type: 'select',
        size: '6',
        api: 'stock-items',
        displayField: (item) => `${item.materialNumber || 'N/A'} - ${item.description || ''}`,
        required: true,
      },
    ],
    [],
  );

  const supplyFields = useMemo(
    () => {
      const fields = [
        {
          field: 'orderLeadTime',
          label: 'Order Lead Time (Days)',
          type: 'number',
          size: '4',
          mustBePositiveInteger: true,
        },
        {
          field: 'packSize',
          label: 'Pack Size',
          type: 'number',
          size: '4',
          mustBePositiveInteger: true,
        },
        {
          field: 'shelfLife',
          label: 'Shelf Life (Days)',
          type: 'number',
          size: '4',
          mustBePositiveInteger: true,
        },
        {
          field: 'partsCriticalityId',
          label: 'Criticality',
          type: 'select',
          size: '4',
          api: 'parts-criticality',
          displayField: 'name',
        },
        {
          field: 'weight',
          label: `Weight (${weightUom})`,
          type: 'number',
          size: '4',
          mustBePositiveDecimal: true,
          maxDecimalPlaces: 2,
        },
        { field: 'empty3', label: '', type: 'spacer', size: '4' },
        { field: 'hazardous', label: 'Hazardous', type: 'switch', size: '2' },
      ];

      if (newPart.hazardous) {
        fields.push({
          field: 'hazardousId',
          label: 'Hazard Class',
          type: 'select',
          size: '4',
          api: 'hazard-class',
          displayField: (hazardClass) => `${hazardClass?.class ?? ''} - ${hazardClass?.name ?? ''}`,
        });
      }

      return fields;
    }, [showStockItemFields, weightUom, newPart.hazardous]);

  const documentFields = useMemo(
    () => [
      {
        field: 'files',
        label: 'Documents',
        type: 'files',
        size: '12',
        directory: 'partsfiles',
        accept:
          '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.bmp,.gif,.heic,.dwg,.dxf,.svg',
      },
    ],
    [],
  );

  const validationFields = useMemo(
    () => [
      ...generalFields,
      ...supplyFields,
      ...(showStockItemFields && !editingId? stockItemFields : []),
      ...(stockOption === 'existingStock' ? existingStockFields : []),
      ...documentFields,
    ],
    [
      generalFields,
      supplyFields,
      showStockItemFields,
      stockItemFields,
      stockOption,
      existingStockFields,
      documentFields,
    ],
  );

  const { errors, setErrors, validate } = useValidator(validationFields, newPart);

  const { renderField: renderGeneralField } = useFormBuilder(generalFields, newPart, setNewPart, errors);
  const { renderField: renderSupplyField } = useFormBuilder(supplyFields, newPart, setNewPart, errors);
  const { renderField: renderStockField } = useFormBuilder(stockItemFields, newPart, setNewPart, errors);
  const { renderField: renderExistingStockField } = useFormBuilder(
    existingStockFields,
    newPart,
    setNewPart,
    errors,
  );
  const { renderField: renderDocumentField } = useFormBuilder(documentFields, newPart, setNewPart, errors);

  const tableFilters = useMemo(
    () => [
      { field: 'categoryId', type: 'dropDown', label: 'Category', api: 'parts-category', displayField: 'name', menuBarFilter: 1 },
      { field: 'sourceOfSupplyId', type: 'dropDown', label: 'Source of Supply', api: 'manufacturers', displayField: 'manufacturer', menuBarFilter: 1 },
      { field: 'supplierId', type: 'dropDown', label: 'Preferred Supplier', api: 'suppliers', displayField: 'name', menuBarFilter: 1 },
    ],
    [],
  );

  const {
    appliedFilters,
    renderFilterControls
  } = useTableFilters(tableFilters);


  const resetPartsPagination = useCallback(() => {
    setParts([]);
    setPartPage(1);
    setPartHasMore(true);
  }, []);

  const handleSortChange = useCallback(
    (field) => {
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop = 0;
      }
      resetPartsPagination();
      if (sortField === field) {
        if (sortOrder === 'asc') {
          setSortOrder('desc');
        } else if (sortOrder === 'desc') {
          setSortField(null);
          setSortOrder(null);
        } else {
          setSortOrder('asc');
        }
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [resetPartsPagination, sortField, sortOrder],
  );

  useEffect(() => {
    const fetchParts = async () => {
      setPartLoading(true);
      try {
        const pageSize = partPage === 1 ? 25 : 5;
        const resp = await getParts({
          page: partPage,
          pageSize,
          search: searchTerm,
          sortField: sortField || undefined,
          sortOrder: sortOrder || undefined,
          ...appliedFilters,
        });
        setParts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newData = resp.data.filter((r) => !existingIds.has(r.id));
          return [...prev, ...newData];
        });
        setPartHasMore(partPage * pageSize < resp.total);
      } catch (err) {
        console.error('Error fetching parts:', err);
      } finally {
        setPartLoading(false);
      }
    };

    fetchParts();
  }, [partPage, appliedFilters, searchTerm, refreshToken, sortField, sortOrder]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 5 &&
        partHasMore &&
        !partLoading
      ) {
        setPartPage((p) => p + 1);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [partHasMore, partLoading]);

  useEffect(() => {
    resetPartsPagination();
  }, [appliedFilters, searchTerm, sortField, sortOrder, resetPartsPagination]);

  useEffect(() => {
    const loadNextMaterialNumber = async () => {
      try {
        const resp = await getNextStockItemNumber();
        const nextNumber = resp?.materialNumber || resp?.material_number;
        if (nextNumber) {
          setNewPart((prev) => ({ ...prev, materialNumber: nextNumber }));
        }
      } catch (err) {
        console.error('Error fetching next stock item number:', err);
      }
    };

    if (showStockItemFields && !newPart.materialNumber && !newPart.stockOption) {
      loadNextMaterialNumber();
    }
  }, [showStockItemFields, newPart.materialNumber, newPart.stockOption]);

  const generateCsv = () => {
    const headers = [
      'OEM Number',
      'Part Number',
      'Description',
      'Category',
      'Manufacturer',
      'Weight',
      'Last Price',
      'Pricing Date',
    ];

    const rows = parts.map((row) => [
      row.oemNumber || '',
      row.partNumber || '',
      row.description || '',
      row.category?.name || '',
      row.sourceOfSupply?.manufacturer || '',
      row.weight ?? '',
      row.lastPrice ?? '',
      row.pricingDate ? row.pricingDate.slice(0, 10) : '',
    ]);

    const csvLines = [
      headers.join(','),
      ...rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')),
    ];
    return csvLines.join('\n');
  };

  const handleExportExcel = () => {
    const headers = [
      'OEM Number',
      'Part Number',
      'Description',
      'Category',
      'Manufacturer',
      'Weight',
      'Last Price',
      'Pricing Date',
    ];

    const rows = parts.map((row) => [
      row.oemNumber || '',
      row.partNumber || '',
      row.description || '',
      row.category?.name || '',
      row.sourceOfSupply?.manufacturer || '',
      row.weight ?? '',
      row.lastPrice ?? '',
      row.pricingDate ? row.pricingDate.slice(0, 10) : '',
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Parts');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'parts.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csv = generateCsv();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'parts.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const mapPartForForm = (part) => ({
    ...part,
    lastCost: part.lastPrice ?? part.lastCost ?? '',
    pricingDate: part.pricingDate ? part.pricingDate.slice(0, 10) : '',
    files: part.files || [],
    stockedItem: part.stockedItem ? 1 : 0,
    stockItemId: part.stockItemId ?? part.stockItem?.id ?? null,
    materialNumber: part.stockItem?.materialNumber ?? '',
    reorderPoint: part.stockItem?.reorderPoint ?? '',
    stockMin: part.stockItem?.stockMin ?? '',
    stockMax: part.stockItem?.stockMax ?? '',
    unitCost: part.stockItem?.unitCost ?? part.lastPrice ?? '',
    measurementUnitId: part.stockItem?.measurementUnitId ?? 5,
    currencyId: part.stockItem?.currencyId ?? part.supplier?.currencyId ?? undefined,
    serialised: part.stockItem?.serialised ?? 0,
    consignment: part.stockItem?.consignment ?? 0,
    sourceOfSupplyId: part.sourceOfSupplyId ?? part.sourceOfSupply?.id,
    supplierId: part.supplierId ?? part.supplier?.id,
    partsCriticalityId: part.partsCriticalityId ?? part.partsCriticality?.id,
    hazardous: part.hazardous ?? 0,
    hazardousId: part.hazardousId ?? part.hazardClass?.id,
  });

  const handleEdit = async (part) => {
    let formatted = part;
    try {
      const fresh = await getPartsById(part.id);
      formatted = fresh;
    } catch (err) {
      console.error('Error loading part for editing', err);
    }
    const mapped = mapPartForForm(formatted);
    const hasLinkedStockItem = mapped.stockItemId !== null && mapped.stockItemId !== undefined;
    if (hasLinkedStockItem) {
      try {
        const stockItemDetails = await getStockItemsById(mapped.stockItemId);
        const quantityDetails = stockItemDetails?.stockItemsQuantities?.[0];
        setLinkedStockItem(stockItemDetails);
        setLinkedStockQuantities(
          consolidateStockQuantities(stockItemDetails?.stockItemsQuantities || []),
        );
        const updated = {
          ...mapped,
          materialNumber: stockItemDetails?.materialNumber ?? mapped.materialNumber,
          reorderPoint: stockItemDetails?.reorderPoint ?? mapped.reorderPoint,
          stockMin: stockItemDetails?.stockMin ?? mapped.stockMin,
          stockMax: stockItemDetails?.stockMax ?? mapped.stockMax,
          unitCost: stockItemDetails?.unitCost ?? mapped.unitCost,
          measurementUnitId: stockItemDetails?.measurementUnitId ?? mapped.measurementUnitId,
          cycleCountFreq: stockItemDetails?.cycleCountFreq ?? mapped.cycleCountFreq,
          currencyId: stockItemDetails?.currencyId ?? mapped.currencyId,
          serialised: stockItemDetails?.serialised ?? mapped.serialised,
          consignment: stockItemDetails?.consignment ?? mapped.consignment,
          quantity: stockItemDetails?.quantity ?? mapped.quantity,
          locationId: quantityDetails?.warehouseId ?? mapped.locationId,
          zone: quantityDetails?.zone ?? mapped.zone,
          aisle: quantityDetails?.aisle ?? mapped.aisle,
          bay: quantityDetails?.bay ?? mapped.bay,
          shelf: quantityDetails?.shelf ?? mapped.shelf,
          stockItemQuantityId: quantityDetails?.id,
        };
        setNewPart(updated);
      } catch (err) {
        console.error('Error loading linked stock item', err);
        setLinkedStockItem(null);
        setLinkedStockQuantities([]);
        setNewPart(mapped);
      }
    } else {
      setLinkedStockItem(null);
      setLinkedStockQuantities([]);
      setNewPart(mapped);
    }
    const supplier = formatted?.supplier || mapped?.supplier || null;
    setSelectedSupplier(
      supplier?.id
        ? {
          value: supplier.id,
          label: supplier.name || 'Supplier',
          raw: supplier,
        }
        : null,
    );
    setActiveTab('general');
    setCreateStockItem(Boolean(mapped.stockedItem));
    setStockOption(mapped.stockedItem ? 'createStock' : 'partOnly');
    setErrors({});
    setEditingId(part.id);
    const incomingRotables = Array.isArray(formatted?.rotables) ? formatted.rotables : [];
    setRotables(
      incomingRotables.filter(
        (rot) => rot?.partId === part.id || rot?.part?.id === part.id,
      ),
    );
    setPendingRotableIds([]);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete part?')) {
      await deleteParts(id);
      setParts((prev) => prev.filter((p) => p.id !== id));
    }
  };

  const handleStockToggle = (value) => {
    if (editingId && newPart.stockedItem) return;
    setStockOption(value);
    const shouldCreateStock = value === 'createStock';
    setCreateStockItem(shouldCreateStock);
    setNewPart((prev) => ({
      ...prev,
      stockedItem: shouldCreateStock ? 1 : 0,
      stockItemId: value === 'existingStock' ? prev.stockItemId ?? null : null,
    }));
  };

  const handleFileDelete = (_key, index) => {
    setNewPart((prev) => {
      const updatedFiles = Array.isArray(prev.files) ? [...prev.files] : [];
      updatedFiles.splice(index, 1);
      return { ...prev, files: updatedFiles };
    });
  };

  const cleanupPendingRotables = useCallback(async () => {
    if (!pendingRotableIds.length) return;

    const idsToDelete = [...pendingRotableIds];
    await Promise.all(
      idsToDelete.map((id) =>
        deleteRotable(id).catch((err) => console.error('Unable to delete rotable', err)),
      ),
    );
    setPendingRotableIds([]);
    setRotables((prev) => prev.filter((rot) => !idsToDelete.includes(rot.id)));
  }, [pendingRotableIds]);

  const handleRotableSave = async (data) => {
    try {
      const stockItemId = Number(newPart.stockItemId ?? newPart.stockItem?.id) || null;
      const partId = Number(editingId ?? newPart.id ?? data.partId) || null;
      const payload = {
        ...data,
        stockItemId,
        partId,
      };

      const created = await createRotable(payload);
      const enriched = created?.id ? await getRotableById(created.id) : created;
      setRotables((prev) => [...prev, enriched]);
      if (enriched?.id) {
        setPendingRotableIds((prev) => [...prev, enriched.id]);
      }
      setShowRotableModal(false);
    } catch (err) {
      console.error('Unable to save rotable', err);
    }
  };

  const handleRotableDelete = async (id) => {
    if (!id) return;
    try {
      await deleteRotable(id);
      setRotables((prev) => prev.filter((rot) => rot.id !== id));
      setPendingRotableIds((prev) => prev.filter((pendingId) => pendingId !== id));
    } catch (err) {
      console.error('Unable to delete rotable', err);
    }
  };

  const updateRotablesWithStockItem = async (stockItemId) => {
    if (!stockItemId) return;
    const updates = rotables.map(async (rotable) => {
      if (rotable.stockItemId) return rotable;
      try {
        const updated = await updateRotable(rotable.id, { stockItemId });
        return updated;
      } catch (err) {
        console.error('Unable to update rotable with stock item', err);
        return rotable;
      }
    });

    const results = await Promise.all(updates);
    setRotables(results.filter(Boolean));
  };

  const filteredRotables = useMemo(() => {
    const partId = Number(editingId ?? newPart.id) || null;
    if (!partId) return rotables;
    return rotables.filter((rotable) => rotable?.partId === partId || rotable?.part?.id === partId);
  }, [editingId, newPart.id, rotables]);

  const buildLocationCode = useCallback((aisle, bay, shelf, zone) => {
    const parts = [aisle, bay, shelf, zone]
      .map((value) => (typeof value === 'string' ? value.trim() : value))
      .filter((value) => Boolean(value));
    return parts.length ? parts.join('-') : null;
  }, []);

  const consolidateStockQuantities = useCallback((quantities = []) => {
    const grouped = new Map();
    quantities.forEach((quantity) => {
      const key = [
        quantity.stockItemId,
        quantity.warehouseId ?? 'null',
        quantity.locationCode ?? 'null',
        quantity.stockItemStatusId ?? 'null',
      ].join('|');
      const quantityValue = Number(quantity.quantity ?? 0);
      const existing = grouped.get(key);

      if (existing) {
        existing.quantity = Number(existing.quantity) + quantityValue;
      } else {
        grouped.set(key, {
          warehouseName: quantity.warehouse?.name ?? '',
          statusName: quantity.stockItemStatus?.name ?? '',
          aisle: quantity.aisle ?? '',
          bay: quantity.bay ?? '',
          shelf: quantity.shelf ?? '',
          quantity: quantityValue,
        });
      }
    });

    return Array.from(grouped.values()).map((row) => ({
      ...row,
      quantity: row.quantity === 0 ? 0 : row.quantity || '',
    }));
  }, []);

  const linkedStockQrValue = useMemo(() => {
    if (!editingId || !linkedStockItem) return '';
    const data = {
      partNumber: newPart.partNumber,
      oemNumber: newPart.oemNumber,
      weight: newPart.weight,
      id: linkedStockItem.id,
      description: newPart.description,
    };
    return JSON.stringify(data);
  }, [editingId, linkedStockItem, newPart.description, newPart.oemNumber, newPart.partNumber, newPart.weight]);

  const createQuantityRecords = async (stockItemId, quantityValue) => {
    if (!stockItemId) return;
    const quantity = quantityValue ?? 0;
    const serialNumbers = rotables.length ? rotables.map((rotable) => rotable.serialNumber) : [null];
    const locationCode = buildLocationCode(newPart.aisle, newPart.bay, newPart.shelf, newPart.zone);
    const warehouseId =
      typeof newPart.locationId === 'number'
        ? newPart.locationId
        : newPart.locationId
          ? Number(newPart.locationId)
          : null;

    await Promise.all(
      serialNumbers.map((serialNumber) =>
        createStockItemsQuantities({
          id: null,
          stockItemId,
          type: 'In Stock',
          workOrderPartsId: null,
          workOrderId: null,
          quantity,
          serialNumber: serialNumber || null,
          movementReason: 'Adjustment',
          notes: '',
          warehouseId,
          aisle: newPart.aisle || null,
          bay: newPart.bay || null,
          shelf: newPart.shelf || null,
          zone: newPart.zone || null,
          locationCode,
        }).catch((err) => console.error('Unable to create stock quantity', err)),
      ),
    );
  };

  const handleSave = async () => {
    const isValid = validate();
    if (!isValid) return;

  
    const sanitizedPartNumber = (newPart.partNumber ?? '').trim();
    const sanitizedOemNumber = (newPart.oemNumber ?? '').trim();
    const hasRotables = rotables.length > 0;
    const normalizeNumber = (value) => {
      if (value === undefined || value === null || value === '') return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    try {
      const duplicateCheck = await checkDuplicatePart({
        partNumber: sanitizedPartNumber,
        oemNumber: sanitizedOemNumber,
        sourceOfSupplyId: newPart.sourceOfSupplyId,
        excludeId: editingId,
      });

      const duplicateErrors = {};
      if (duplicateCheck?.partSourceExists) {
        duplicateErrors.partNumber = 'A part with this Part Number and Source of Supply already exists.';
        duplicateErrors.sourceOfSupplyId = 'A part with this Part Number and Source of Supply already exists.';
      }

      if (duplicateCheck?.oemSourceExists) {
        duplicateErrors.oemNumber = 'An OEM part with this Source of Supply already exists.';
        duplicateErrors.sourceOfSupplyId = duplicateErrors.sourceOfSupplyId || 'An OEM part with this Source of Supply already exists.';
      }

      if (Object.keys(duplicateErrors).length) {
        setErrors((prev) => ({ ...prev, ...duplicateErrors }));
        setActiveTab('general');
        return;
      }

      const partPayload = {
        partNumber: sanitizedPartNumber,
        oemNumber: sanitizedOemNumber,
        description: newPart.description,
        categoryId: newPart.categoryId ?? 4,
        sourceOfSupplyId: normalizeNumber(newPart.sourceOfSupplyId),
        supplierId: normalizeNumber(newPart.supplierId),
        pricingDate: newPart.pricingDate || null,
        lastPrice: normalizeNumber(newPart.lastCost),
        weight: normalizeNumber(newPart.weight),
        shelfLife: normalizeNumber(newPart.shelfLife),
        hazardous: newPart.hazardous ? 1 : 0,
        hazardousId: newPart.hazardous ? normalizeNumber(newPart.hazardousId) : null,
        orderLeadTime: normalizeNumber(newPart.orderLeadTime),
        packSize: normalizeNumber(newPart.packSize),
        partsCriticalityId: normalizeNumber(newPart.partsCriticalityId),
        files: newPart.files || [],
        /*stockedItem:
          stockOption === 'existingStock' || showStockItemFields ? 1 : 0,
        stockItemId: stockOption === 'existingStock'
          ? normalizeNumber(newPart.stockItemId)
          : existingStockItemId,*/
      };

      const stockPayload = showStockItemFields
        ? {
          materialNumber: newPart.materialNumber,
          reorderPoint: normalizeNumber(newPart.reorderPoint),
          stockMin: normalizeNumber(newPart.stockMin),
          stockMax: normalizeNumber(newPart.stockMax),
          unitCost:
            newPart.unitCost === undefined || newPart.unitCost === ''
              ? normalizeNumber(newPart.lastCost)
              : normalizeNumber(newPart.unitCost),
          measurementUnitId: normalizeNumber(newPart.measurementUnitId),
          cycleCountFreq: normalizeNumber(newPart.cycleCountFreq),
          currencyId: normalizeNumber(newPart.currencyId),
          serialised: hasRotables ? 1 : newPart.serialised ? 1 : 0,
          consignment: newPart.consignment ? 1 : 0,
          description: newPart.description,
        }
        : undefined;

      let createdPart;

      if (stockOption === 'existingStock') {
        if (!partPayload.stockItemId) {
          setErrors((prev) => ({
            ...prev,
            form: 'Please select an existing stocked item.',
          }));
          return;
        }

        await addPartToStockItem({
          part: partPayload,
          stockItemId: partPayload.stockItemId,
        });
      } else if (editingId) {
        await updateParts(editingId, {
          ...partPayload,
        });
      } else {
        createdPart = await createStockItemAndPart({
          part: partPayload,
          stockItem: stockPayload,
          createStockItem: showStockItemFields,
        });
      }

      const createdStockItemId = createdPart?.stockItemId || null;

      if (!editingId && showStockItemFields && createdStockItemId) {
        await updateRotablesWithStockItem(createdStockItemId);
        await createQuantityRecords(createdStockItemId, normalizeNumber(newPart.quantity));
      }

      setShowModal(false);
      setEditingId(null);
      setActiveTab('general');
      setCreateStockItem(false);
      setStockOption('partOnly');
      setRotables([]);
      setPendingRotableIds([]);
      setNewPart(getDefaultPartState());
      setSelectedSupplier(null);
      setLinkedStockItem(null);
      setLinkedStockQuantities([]);
      setErrors({});
      setParts([]);
      setPartPage(1);
      setPartHasMore(true);
      setRefreshToken((token) => token + 1);
    } catch (error) {
      console.error('Error saving part:', error);
      const responseMessage = error.response?.data?.message;
      const normalizedMessage = Array.isArray(responseMessage)
        ? responseMessage.join(' ')
        : responseMessage || error.message;

      setErrors((prev) => ({
        ...prev,
        form:
          typeof normalizedMessage === 'string'
            ? normalizedMessage
            : 'Unable to save part. Please try again.',
      }));
    }
  };

  const handleCloseModal = async () => {
    await cleanupPendingRotables();
    setShowModal(false);
    setErrors({});
    setActiveTab('general');
    setCreateStockItem(false);
    setStockOption('partOnly');
    setRotables([]);
    setPendingRotableIds([]);
    setNewPart(getDefaultPartState());
    setSelectedSupplier(null);
    setEditingId(null);
    setLinkedStockItem(null);
    setLinkedStockQuantities([]);
  };

  // const stockSwitchDisabled = editingId && Boolean(newPart.stockedItem);
  const stockSwitchDisabled = 0;
  const partOnlyLabel = editingId ? 'Part Only' : 'Create Part Only';

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center">
        <div className="d-flex flex-wrap align-items-center gap-2 ms-3">
          {renderFilterControls()}
        </div>

        <div className="d-flex align-items-center gap-2 ms-auto me-8">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="dropdown me-2">
          <button
            className="btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            Export Data
          </button>
          <ul className="dropdown-menu p-12 border bg-base shadow">
            <li>
              <button className="dropdown-item" onClick={handleExportExcel}>
                Excel
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={handleExportCSV}>
                CSV
              </button>
            </li>
          </ul>
        </div>

        {userProfile?.privileges?.includes('parts:create') && (
          <button
              className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewPart(getDefaultPartState());
              setEditingId(null);
              setErrors({});
              setActiveTab('general');
              setCreateStockItem(false);
              setStockOption('partOnly');
              setRotables([]);
              setPendingRotableIds([]);
              setSelectedSupplier(null);
              setLinkedStockItem(null);
              setLinkedStockQuantities([]);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" /> Add Part
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div
          className="paginated-table-container scroll-sm"
          ref={listContainerRef}
          style={{ maxHeight: 'calc(100vh - 215px)' }}
        >
          <PartsDataTable
            parts={parts}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={partLoading}
            useModule={useModule}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSortChange}
          />
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Part' : 'New Part'}</h5>
                <button className="btn-close" onClick={handleCloseModal} />
              </div>
              <div className="modal-body">
                {errors.form && (
                  <div className="alert alert-danger" role="alert">
                    {errors.form}
                  </div>
                )}
                <ul className="nav nav-tabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                      type="button"
                      role="tab"
                      onClick={() => setActiveTab('general')}
                    >
                      General
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'supply' ? 'active' : ''}`}
                      type="button"
                      role="tab"
                      onClick={() => setActiveTab('supply')}
                    >
                      Storage Requirments
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button
                      className={`nav-link ${activeTab === 'documents' ? 'active' : ''}`}
                      type="button"
                      role="tab"
                      onClick={() => setActiveTab('documents')}
                    >
                      Documents
                    </button>
                  </li>
                </ul>

                <div className="tab-content pt-3">
                  <div
                    className={`tab-pane fade ${activeTab === 'general' ? 'show active' : ''}`}
                    role="tabpanel"
                  >
                    <div className="row g-3">{generalFields.map(renderGeneralField)}</div>
                    {editingId ? (
                      <div className="mt-24 mb-10">
                        
                        {linkedStockItem && (
                          <>
                          <label className="form-label d-block text-lg mb-16">Stock Item Details</label>
                            <div className="row g-3">
                              <div className="col-md-4">
                                <label className="form-label">Material Number</label>
                                <input
                                  className="form-control"
                                  value={linkedStockItem?.materialNumber ?? newPart.materialNumber ?? ''}
                                  readOnly
                                  disabled
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Description</label>
                                <input
                                  className="form-control"
                                  value={linkedStockItem?.description ?? newPart.description ?? ''}
                                  readOnly
                                  disabled
                                />
                              </div>
                              <div className="col-md-2">
                                <label className="form-label">Serialised Part</label>
                                <div className="form-switch switch-primary">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    checked={Boolean(linkedStockItem?.serialised ?? newPart.serialised)}
                                    disabled
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="row g-3 mt-3">
                              <div className="col-12">
                                <div className="d-flex align-items-center justify-content-between">
                                  <label className="form-label d-block text-lg">Warehouse Quantities</label>
                                </div>
                              </div>
                              {linkedStockQuantities.length > 0 ? (
                                linkedStockQuantities.map((row, index) => {
                                  const showLabels = index === 0;
                                  return (
                                    <div className="col-12" key={`linked-quantity-row-${index}`}>
                                      <div className="row g-3 align-items-end">
                                        <div className="col-md-3">
                                          {showLabels && <label className="form-label">Warehouse</label>}
                                          <input
                                            className="form-control"
                                            value={row.warehouseName ?? ''}
                                            readOnly
                                            disabled
                                          />
                                        </div>
                                        <div className="col-md-3">
                                          {showLabels && <label className="form-label">Status</label>}
                                          <input
                                            className="form-control"
                                            value={row.statusName ?? ''}
                                            readOnly
                                            disabled
                                          />
                                        </div>
                                        <div className="col-md-1">
                                          {showLabels && <label className="form-label">Aisle</label>}
                                          <input
                                            className="form-control"
                                            value={row.aisle ?? ''}
                                            readOnly
                                            disabled
                                          />
                                        </div>
                                        <div className="col-md-1">
                                          {showLabels && <label className="form-label">Bay</label>}
                                          <input
                                            className="form-control"
                                            value={row.bay ?? ''}
                                            readOnly
                                            disabled
                                          />
                                        </div>
                                        <div className="col-md-1">
                                          {showLabels && <label className="form-label">Shelf</label>}
                                          <input
                                            className="form-control"
                                            value={row.shelf ?? ''}
                                            readOnly
                                            disabled
                                          />
                                        </div>
                                        <div className="col-md-2">
                                          {showLabels && <label className="form-label">Quantity</label>}
                                          <input
                                            type="number"
                                            className="form-control"
                                            value={row.quantity ?? ''}
                                            readOnly
                                            disabled
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="col-12">
                                  <p className="text-muted mb-0">No warehouse quantities available.</p>
                                </div>
                              )}
                              <div className="col-12">
                                <label className="form-label">Notes</label>
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  value={linkedStockItem?.notes ?? linkedStockItem?.stockDescription ?? ''}
                                  readOnly
                                  disabled
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Stock Image</label>
                                {linkedStockItem?.stockImage ? (
                                  <div className="text-left">
                                    <img
                                      src={`${process.env.REACT_APP_SPACES_BASE}/${linkedStockItem.stockImage}`}
                                      alt="Stock"
                                      style={{
                                        height: '200px',
                                        objectFit: 'cover',
                                        borderRadius: '6px',
                                        border: '1px solid #ccc',
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <p className="text-muted mb-0">No stock image available.</p>
                                )}
                              </div>
                              <div className="col-md-6 d-flex align-items-end justify-content-end mb-2 pe-12">
                                {linkedStockQrValue && <QRCode value={linkedStockQrValue} size={128} />}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="mt-24 mb-10">
                          <label className="form-label d-block text-xl mb-16">Stock Options</label>
                          {isLinkedStockItemEdit ? (
                            <div className="form-switch switch-primary d-flex align-items-center gap-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked
                                disabled
                              />
                              <label className="form-check-label">Stock Item</label>
                            </div>
                          ) : (
                            <div className="d-flex flex-wrap gap-4">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="stockOption"
                                  id="partOnlyOption"
                                  checked={stockOption === 'partOnly'}
                                  onChange={() => handleStockToggle('partOnly')}
                                  disabled={stockSwitchDisabled}
                                />
                                <label className="form-check-label ms-8" htmlFor="partOnlyOption">
                                  {partOnlyLabel}
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="stockOption"
                                  id="createStockOption"
                                  checked={stockOption === 'createStock'}
                                  onChange={() => handleStockToggle('createStock')}
                                  disabled={stockSwitchDisabled}
                                />
                                <label className="form-check-label ms-8" htmlFor="createStockOption">
                                  Make this a Stocked Item
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                        {stockOption === 'existingStock' && (
                          <div className="row g-3 mt-2">
                            {existingStockFields.map(renderExistingStockField)}
                          </div>
                        )}
                        {showStockItemFields && (
                          <>
                            <div className="row g-3 mt-2">{stockItemFields.map(renderStockField)}</div>
                            {useRotables && !editingId && (
                              <div className="mt-3">
                                <button
                                  className="btn btn-outline-primary"
                                  type="button"
                                  onClick={() => setShowRotableModal(true)}
                                >
                                  <Icon icon="mage:plus" className="me-2 text-lg" style={{ position: "relative", top: "-3px"}}/> Make this Parts a Rotable
                                </button>
                              </div>
                            )}
                            {filteredRotables.length > 0 && (
                              <div className="table-responsive mt-3">
                                <table className="table table-sm">
                                  <thead>
                                    <tr>
                                      <th>Serial Number</th>
                                      <th>Part Number</th>
                                      <th>Status</th>
                                      <th className="text-end">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredRotables.map((rotable) => (
                                      <tr key={rotable.id}>
                                        <td>{rotable.serialNumber || '-'}</td>
                                        <td>{rotable.part?.partNumber ?? rotable.partNumber ?? '-'}</td>
                                        <td>{rotable.status?.name || '-'}</td>
                                        <td className="text-end">
                                          <button
                                            type="button"
                                            className="btn btn-link text-danger p-0"
                                            onClick={() => handleRotableDelete(rotable.id)}
                                          >
                                            Remove
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {editingId && filteredRotables.length > 0 && (
                      <div className="table-responsive mt-3">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Serial Number</th>
                              <th>Part Number</th>
                              <th>Status</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRotables.map((rotable) => (
                              <tr key={rotable.id}>
                                <td>{rotable.serialNumber || '-'}</td>
                                <td>{rotable.part?.partNumber ?? rotable.partNumber ?? '-'}</td>
                                <td>{rotable.status?.name || '-'}</td>
                                <td className="text-end">
                                  <button
                                    type="button"
                                    className="btn btn-link text-danger p-0"
                                    onClick={() => handleRotableDelete(rotable.id)}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div
                    className={`tab-pane fade ${activeTab === 'supply' ? 'show active' : ''}`}
                    role="tabpanel"
                  >
                    <div className="row g-3">{supplyFields.map(renderSupplyField)}</div>
                  </div>

                  <div
                    className={`tab-pane fade ${activeTab === 'documents' ? 'show active' : ''}`}
                    role="tabpanel"
                  >
                    <div className="row g-3">{documentFields.map(renderDocumentField)}</div>
                    {Array.isArray(newPart.files) && newPart.files.length > 0 && (
                      <DocumentDataTable files={newPart.files} onDelete={handleFileDelete} />
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseModal}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showRotableModal && (
        <RotableFormModal
          show={showRotableModal}
          onClose={() => setShowRotableModal(false)}
          onSave={handleRotableSave}
          initialValues={
            editingId
              ? {
                partId: editingId,
                part: {
                  id: editingId,
                  partNumber: newPart.partNumber,
                  description: newPart.description,
                },
              }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default PartsListLayer;
